import React, { useState, useEffect } from 'react';
import {
  Plus, FileText, Users, BarChart3, ChevronRight,
  TrendingUp, Target, Loader2, GraduationCap
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer, Legend
} from 'recharts';
import { useAuth } from '../../lib/AuthContext';
import {
  getAssignments, getClassAnalytics, getContentAlerts,
  getGCCourses, getGCCourseStudents, getGCStatus, getGCAuthUrl, getMyClasses
} from '../../lib/api';
import { DraftyMascot } from '../DraftyMascot';
import { CreateAssignmentModal } from './CreateAssignmentModal';
import { SetupCard } from './SetupCard';
import { ARGUMENTATIVE_TEMPLATE } from '../../lib/assignmentTemplates';
import { studentLabel } from '../../lib/format';
import { BRAND } from '../../lib/brandColors';
import { toast } from 'sonner';

export const TeacherOverview = ({ onTabChange }) => {
  const { profile, isDemo } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [guardAlerts, setGuardAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  // Setup-card "Create your first assignment" opens the modal pre-filled
  const [modalTemplate, setModalTemplate] = useState(null);
  const [myClasses, setMyClasses] = useState([]);

  // Classroom state
  const [classroomCourses, setClassroomCourses] = useState([]);
  const [classroomStudents, setClassroomStudents] = useState([]);
  const [classroomLoading, setClassroomLoading] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [gcConnected, setGcConnected] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [assignmentsData, analyticsData, alertsData, gcStatusData, classesData] = await Promise.all([
          getAssignments(profile?.id),
          getClassAnalytics(profile?.id),
          getContentAlerts(profile?.id).catch(() => ({ alerts: [] })),
          getGCStatus().catch(() => ({ connected: false })),
          getMyClasses().catch(() => ({ classes: [] })),
        ]);
        setGcConnected(gcStatusData.connected);
        setAssignments(assignmentsData || []);
        setAnalytics(analyticsData);
        setGuardAlerts(alertsData?.alerts || []);
        setMyClasses(Array.isArray(classesData) ? classesData : classesData?.classes || []);
      } catch (error) {
        if (isDemo) {
          setAssignments([]);
          setAnalytics(null);
        } else {
          console.error('Failed to load data:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [profile, isDemo]);

  const radarData = analytics?.standards_breakdown?.length > 0
    ? analytics.standards_breakdown
    : [];

  // mastery/needs_work are raw glow/grow COUNTS from the API, not percentages.
  // Render the true ratio: glow / (glow + grow) across all standards.
  const totalGlow = radarData.reduce((sum, d) => sum + (d.mastery || 0), 0);
  const totalGrow = radarData.reduce((sum, d) => sum + (d.needs_work || 0), 0);
  const avgMastery = totalGlow + totalGrow > 0
    ? Math.round((totalGlow / (totalGlow + totalGrow)) * 100)
    : null;

  const reloadData = async () => {
    try {
      const [assignmentsData, analyticsData, alertsData] = await Promise.all([
        getAssignments(profile?.id),
        getClassAnalytics(profile?.id),
        getContentAlerts(profile?.id).catch(() => ({ alerts: [] }))
      ]);
      setAssignments(assignmentsData || []);
      setAnalytics(analyticsData);
      setGuardAlerts(alertsData?.alerts || []);
    } catch (error) {
      console.error('Failed to reload data:', error);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-400">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* First-run 3-step setup card — disappears forever once completed */}
      {!isDemo && (
        <SetupCard
          profile={profile}
          assignments={assignments}
          classes={myClasses}
          gcConnected={gcConnected}
          onCreateAssignment={() => {
            setModalTemplate(ARGUMENTATIVE_TEMPLATE);
            setShowCreateModal(true);
          }}
          onGoToClassroom={() => onTabChange?.('classroom')}
          onClassCreated={(cls) => setMyClasses(prev => [...prev, cls])}
        />
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-5 gap-4" aria-label="Class statistics">
        <div className="card-surface p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-flow/20 rounded-lg">
              <FileText className="text-teal-flow" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{assignments.length}</p>
              <p className="text-slate-400 text-sm">Active Assignments</p>
            </div>
          </div>
        </div>
        <div className="card-surface p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-ai-accent/20 rounded-lg">
              <Users className="text-ai-accent" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{analytics?.unique_students || 0}</p>
              <p className="text-slate-400 text-sm">Students</p>
            </div>
          </div>
        </div>
        <div className="card-surface p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gold-bright/20 rounded-lg">
              <BarChart3 className="text-gold-bright" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{analytics?.total_feedback_items || 0}</p>
              <p className="text-slate-400 text-sm">Feedback Given</p>
            </div>
          </div>
        </div>
        <div className="card-surface p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <TrendingUp className="text-green-500" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{avgMastery !== null ? `${avgMastery}%` : '—'}</p>
              <p className="text-slate-400 text-sm">Avg. Mastery</p>
            </div>
          </div>
        </div>
        <div className="card-surface p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${gcConnected ? 'bg-green-500/20' : 'bg-slate-700/50'}`}>
              <GraduationCap className={gcConnected ? 'text-green-500' : 'text-slate-500'} size={20} />
            </div>
            <div>
              <p className={`text-sm font-bold ${gcConnected ? 'text-green-400' : 'text-slate-500'}`}>
                {gcConnected === null ? '...' : gcConnected ? 'Connected' : 'Not Connected'}
              </p>
              <p className="text-slate-400 text-sm">Google Classroom</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-6">
        {/* CCSS Radar Chart */}
        <div className="card-surface p-6">
          <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Target className="text-ai-accent" size={20} />
            CCSS Mastery Overview
          </h3>
          <div className="h-64">
            {radarData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-500">
                <p className="text-sm">Standards mastery data will appear here as students receive AI feedback.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#334155" />
                  <PolarAngleAxis dataKey="standard" tick={{ fill: BRAND.textSecondaryDark, fontSize: 12 }} />
                  <PolarRadiusAxis tick={{ fill: '#64748b' }} />
                  <Radar
                    name="Mastery"
                    dataKey="mastery"
                    stroke={BRAND.tealFlow}
                    fill={BRAND.tealFlow}
                    fillOpacity={0.3}
                  />
                  <Radar
                    name="Needs Work"
                    dataKey="needs_work"
                    stroke={BRAND.goldBright}
                    fill={BRAND.goldBright}
                    fillOpacity={0.3}
                  />
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card-surface p-6">
          <h3 className="font-heading text-lg font-semibold text-white mb-4">Recent Activity</h3>
          {analytics?.recent_feedback?.length > 0 ? (
            <div className="space-y-3">
              {analytics.recent_feedback.slice(0, 8).map((fb, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="badge-ccss">{fb.ccss_standard || 'General'}</span>
                    <div>
                      <p className="text-white text-sm">
                        {fb.feedback_type === 'comprehensive' ? 'AI coaching feedback generated' : 'Feedback received'}
                      </p>
                      <p className="text-slate-500 text-xs">{studentLabel(fb)}</p>
                    </div>
                  </div>
                  <span className="text-slate-500 text-xs">{new Date(fb.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-slate-500 text-sm">Activity will appear here as students use the AI writing coach and submit drafts.</p>
            </div>
          )}
        </div>
      </div>

      {/* Assignments List */}
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-white">Your Assignments</h3>
          <button
            data-testid="create-assignment-btn"
            onClick={() => setShowCreateModal(true)}
            className="btn-teal px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={18} />
            New Assignment
          </button>
        </div>

        {assignments.length === 0 ? (
          <div className="text-center py-8">
            <DraftyMascot size="md" mood="thinking" className="mx-auto mb-4" />
            {isDemo ? (
              <>
                <p className="text-slate-300 font-medium mb-1">Welcome to the DraftFlow teacher demo!</p>
                <p className="text-slate-400 text-sm">Sign in with Google to create assignments, generate rubrics, and track student writing progress.</p>
              </>
            ) : (
              <>
                <p className="text-slate-400 mb-4">Your writing assignments will live here — prompts, due dates, standards, and student progress.</p>
                <button
                  data-testid="empty-create-assignment"
                  onClick={() => { setModalTemplate(ARGUMENTATIVE_TEMPLATE); setShowCreateModal(true); }}
                  className="btn-teal px-4 py-2 rounded-lg inline-flex items-center gap-2"
                >
                  <Plus size={18} />
                  Create your first assignment
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3" role="list">
            {assignments.map((assignment, idx) => (
              <div
                key={assignment.id}
                role="listitem"
                className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg hover:bg-slate-900 transition-colors"
              >
                <div>
                  <h4 className="text-white font-medium">{assignment.title}</h4>
                  <p className="text-slate-400 text-sm">{assignment.prompt?.slice(0, 60)}...</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-slate-500 text-sm">
                    {assignment.due_date ? new Date(assignment.due_date).toLocaleDateString() : 'No due date'}
                  </span>
                  <ChevronRight className="text-slate-600" size={18} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Google Classroom Integration */}
      <div className="card-surface p-6">
        <h3 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <GraduationCap className="text-teal-flow" size={20} />
          Google Classroom
        </h3>
        {!gcConnected ? (
          <div className="flex items-center justify-between">
            <p className="text-slate-400 text-sm">Connect your Google Classroom to view your course rosters.</p>
            <button
              data-testid="connect-classroom-btn"
              aria-label="Connect Google Classroom"
              onClick={async () => {
                try {
                  const { url } = await getGCAuthUrl();
                  window.open(url, '_blank', 'width=600,height=700,noopener,noreferrer');
                } catch {
                  toast.error('Could not start Google Classroom authorization.');
                }
              }}
              className="btn-teal px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
            >
              <GraduationCap size={16} />
              Connect Classroom
            </button>
          </div>
        ) : classroomCourses.length === 0 ? (
          <div className="flex items-center justify-between">
            <p className="text-slate-400 text-sm">Connected! Load your courses to get started.</p>
            <button
              data-testid="load-courses-btn"
              onClick={async () => {
                setClassroomLoading(true);
                try {
                  const data = await getGCCourses();
                  setClassroomCourses(data.courses || []);
                  if ((data.courses || []).length === 0) {
                    toast.error('No active courses found in your Google Classroom.');
                  }
                } catch (error) {
                  if (error.response?.status === 401) {
                    setGcConnected(false);
                    toast.error('Google Classroom session expired. Please reconnect.');
                  } else {
                    toast.error('Could not load courses.');
                  }
                } finally {
                  setClassroomLoading(false);
                }
              }}
              disabled={classroomLoading}
              className="btn-teal px-4 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
            >
              {classroomLoading ? (
                <><Loader2 size={16} className="animate-spin" /> Loading...</>
              ) : (
                <><GraduationCap size={16} /> Load Courses</>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {classroomCourses.map((course) => (
              <div
                key={course.id}
                className={`p-4 bg-slate-900/50 rounded-lg border cursor-pointer transition-colors ${
                  selectedCourseId === course.id ? 'border-flow-blue/50 bg-flow-blue/5' : 'border-slate-700 hover:bg-slate-900'
                }`}
                onClick={async () => {
                  setSelectedCourseId(course.id);
                  try {
                    const roster = await getGCCourseStudents(course.id);
                    setClassroomStudents(roster.students || []);
                  } catch {
                    toast.error('Could not load roster for this course.');
                  }
                }}
                data-testid={`classroom-course-${course.id}`}
              >
                <h4 className="text-white font-medium text-sm">{course.name}</h4>
                {course.section && <p className="text-slate-400 text-xs mt-1">Section: {course.section}</p>}
              </div>
            ))}

            {selectedCourseId && classroomStudents.length > 0 && (
              <div className="mt-4 bg-slate-900/50 rounded-lg border border-slate-700 p-4">
                <h4 className="text-white font-medium text-sm mb-3">Roster ({classroomStudents.length} students)</h4>
                <div className="grid grid-cols-2 gap-2">
                  {classroomStudents.map((student, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-slate-300 text-sm py-1">
                      <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-xs text-slate-400">
                        {(student.name || student.email || '?')[0].toUpperCase()}
                      </div>
                      <span>{student.name || student.email}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateAssignmentModal
          template={modalTemplate}
          onClose={() => { setShowCreateModal(false); setModalTemplate(null); }}
          onCreated={() => {
            setShowCreateModal(false);
            setModalTemplate(null);
            reloadData();
          }}
        />
      )}
    </div>
  );
};
